// hooks/useDateFilter.js
// Provides preset + custom date range filtering for records with a `weekEnding` field.
import { useState, useMemo } from "react";

// ─── Preset definitions ──────────────────────────────────────────────────────
export const PRESETS = [
  { key: "all", label: "All Time", icon: "∞" },
  { key: "1w", label: "Last Week", icon: "📆" },
  { key: "4w", label: "Last 4 Weeks", icon: "📅" },
  { key: "1m", label: "Last Month", icon: "🗓" },
  { key: "3m", label: "Last Quarter", icon: "📊" },
  { key: "6m", label: "Last 6 Months", icon: "📈" },
  { key: "1y", label: "This Year", icon: "🗃" },
  { key: "custom", label: "Custom Range", icon: "✂️" },
];

function getPresetRange(key) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (key) {
    case "1w": {
      const from = new Date(today);
      from.setDate(from.getDate() - 7);
      return { from, to: today };
    }
    case "4w": {
      const from = new Date(today);
      from.setDate(from.getDate() - 28);
      return { from, to: today };
    }
    case "1m": {
      const from = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        today.getDate(),
      );
      return { from, to: today };
    }
    case "3m": {
      const from = new Date(
        today.getFullYear(),
        today.getMonth() - 3,
        today.getDate(),
      );
      return { from, to: today };
    }
    case "6m": {
      const from = new Date(
        today.getFullYear(),
        today.getMonth() - 6,
        today.getDate(),
      );
      return { from, to: today };
    }
    case "1y": {
      const from = new Date(today.getFullYear(), 0, 1);
      return { from, to: today };
    }
    default:
      return null; // "all" or "custom"
  }
}

// ─── Standalone helper — filter any array of {weekEnding} objects ─────────────
export function applyDateFilter(rows, preset, customFrom, customTo) {
  if (preset === "all") return rows;
  let from, to;
  if (preset === "custom") {
    if (!customFrom && !customTo) return rows;
    from = customFrom ? new Date(customFrom) : null;
    to   = customTo   ? new Date(customTo)   : null;
  } else {
    const range = getPresetRange(preset);
    if (!range) return rows;
    ({ from, to } = range);
  }
  return rows.filter((r) => {
    if (!r.weekEnding) return true; // keep rows without a date
    const d = new Date(r.weekEnding);
    if (from && d < from) return false;
    if (to   && d > to)   return false;
    return true;
  });
}

// ─── Hook ────────────────────────────────────────────────────────────────────
export function useDateFilter(records = []) {
  const [preset, setPreset] = useState("1y"); // default: this year
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const filtered = useMemo(() => {
    if (preset === "all") return records;

    let from, to;

    if (preset === "custom") {
      if (!customFrom && !customTo) return records;
      from = customFrom ? new Date(customFrom) : null;
      to = customTo ? new Date(customTo) : null;
    } else {
      const range = getPresetRange(preset);
      if (!range) return records;
      ({ from, to } = range);
    }

    return records.filter((r) => {
      if (!r.weekEnding) return false;
      const d = new Date(r.weekEnding);
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [records, preset, customFrom, customTo]);

  // Human-readable summary of active range
  const rangeLabel = useMemo(() => {
    if (preset === "all") return "All time";
    if (preset === "custom") {
      if (customFrom && customTo) return `${customFrom} → ${customTo}`;
      if (customFrom) return `From ${customFrom}`;
      if (customTo) return `Until ${customTo}`;
      return "All time";
    }
    return PRESETS.find((p) => p.key === preset)?.label || "";
  }, [preset, customFrom, customTo]);

  return {
    preset,
    setPreset,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    filtered,
    rangeLabel,
    isFiltered: preset !== "all",
    count: filtered.length,
    total: records.length,
  };
}
